import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import { EscrowPartyTypeEnum, ContactTypeEnum, AgreementFormEnum } from 'src/graphql/schema';
import { ContactCreateDocument } from 'src/graphql/actions/contact/contactCreate.mutation';
import { ContactRemoveDocument } from 'src/graphql/actions/contact/contactRemove.mutation';
import { ContactUpdateDocument } from 'src/graphql/actions/contact/contactUpdate.mutation';
import { removeContactCache, addContactCache } from 'src/graphql/client/cache';
import { ClientError, getApiError } from 'src/utils/errors';

import type { IContact, IContactCreateParams, IContactUpdateParams } from 'src/graphql/schema';
import type { IRepresentative } from './types';

type Props = {
  role: EscrowPartyTypeEnum;
  escrowId: string;
  escrowPartyId: string;
  agreementType: AgreementFormEnum;
  contacts: IContact[];
  canUpdate: boolean;
  // Called once a representative is added or updated, with what was saved
  onRepresentativeSaved?: (representative: { signatory?: boolean | null }) => void;
};
/* eslint-disable react/display-name */
export const withApiSubmitActions = (Component: React.ElementType, getProps: () => Props) => () => {
  const { role, escrowId, escrowPartyId, agreementType, contacts, canUpdate, onRepresentativeSaved } = getProps();

  const [createContact] = useMutation(ContactCreateDocument);
  const [removeContact] = useMutation(ContactRemoveDocument);
  const [updateContact] = useMutation(ContactUpdateDocument);

  const onCreate = async (representative: IContactCreateParams) => {
    try {
      const { data } = await createContact({
        variables: {
          escrowPartyId,
          contactParams: {
            ...representative,
            signatory: representative.signatory ?? false,
            contactType: ContactTypeEnum.Administrative,
          },
        },
      });

      if (data?.contactCreate?.success) {
        toast.success('Contact added successfully');

        if (data?.contactCreate?.contact)
          addContactCache({
            escrowId,
            partyType: role,
            partyId: escrowPartyId,
            contact: {
              id: data?.contactCreate?.contact?.id,
              name: data?.contactCreate?.contact.name,
              email: data?.contactCreate?.contact.email,
              phone: data?.contactCreate?.contact.phone,
              signatory: data?.contactCreate?.contact.signatory,
              contactType: data?.contactCreate?.contact.contactType,
            },
          });

        onRepresentativeSaved?.(representative);
      } else throw new ClientError(data?.contactCreate?.errors?.[0]);
    } catch (error) {
      getApiError(error, toast.error);
      throw error;
    }
  };

  const onRemove = async (contactId: string) => {
    try {
      await removeContact({
        variables: {
          contactId,
        },
      });
      removeContactCache({ partyType: role, partyId: escrowPartyId, contactId });
      toast.success('Representative removed!', {
        description: 'They will no longer be listed in the escrow agreement.',
      });
    } catch (error) {
      toast.error('Failed to remove contact');
      throw error;
    }
  };

  const onUpdate = async (id: string, representative: IContactUpdateParams) => {
    try {
      await updateContact({
        variables: {
          contactId: id,
          contactParams: representative,
        },
      });
      toast.success('Contact updated successfully');
      onRepresentativeSaved?.(representative);
    } catch (error) {
      toast.error('Failed to update contact');
      throw error;
    }
  };

  const submit = {
    add: onCreate,
    remove: onRemove,
    edit: onUpdate,
  };
  const representatives = getRepresentatives(contacts ?? []);

  return (
    <Component
      role={role}
      agreementType={agreementType}
      submit={submit}
      representatives={representatives}
      currentPolicyRole={{
        update: canUpdate,
      }}
    />
  );
};

function getRepresentatives(contacts: IContact[]): IRepresentative[] {
  return contacts.map(({ id, name, email, phone, signatory }) => ({
    id,
    name,
    email,
    phone: phone ?? '',
    signatory,
  }));
}
